import { useState, type MouseEvent } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Task } from "../types";
import { PlaneIssueBadge } from "./PlaneIssueBadge";
import { usePushTaskToPlane } from "./usePushTaskToPlane";

// Single-task push to the engineering Plane tracker. Only Dev-category tasks can
// be pushed. Once linked, shows the linked badge + a Re-sync action. Rendered
// both in the task detail modal and inline on Action Items rows, so it stops
// click propagation (otherwise an inline click would open/toggle the row).
export const PushToPlaneButton = ({
  task,
  className,
}: {
  task: Task;
  className?: string;
}) => {
  const pushTask = usePushTaskToPlane();
  const [pushing, setPushing] = useState(false);

  // Linked tasks keep their badge + Re-sync regardless of category (category
  // can be edited after linking); only the INITIAL push is dev-gated.
  if (!task.plane_issue_id && task.category !== "dev") return null;

  const handlePush = async (e: MouseEvent) => {
    e.stopPropagation();
    setPushing(true);
    try {
      await pushTask(task.id);
    } finally {
      setPushing(false);
    }
  };

  return (
    <span
      className={cn("inline-flex items-center gap-1.5 align-middle", className)}
    >
      {task.plane_issue_id ? (
        <>
          <PlaneIssueBadge task={task} />
          {/* type="button": the task detail modal renders this inside its
              edit form, where a bare button submits and closes the modal. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handlePush}
            disabled={pushing}
            className="h-6 px-1.5 text-xs cursor-pointer"
          >
            {pushing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Re-sync"}
          </Button>
        </>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePush}
          disabled={pushing}
          className="h-6 px-2 text-xs cursor-pointer gap-1"
        >
          {pushing ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Send className="w-3 h-3" />
          )}
          Push to Plane
        </Button>
      )}
    </span>
  );
};
